import { createContext, useContext } from 'react';
import { ApoHoloSiteDto } from '../models/ApoHoloSiteDto';
import { MolstarAction, MolstarRepresentationChangePayload } from './ControlTypes';

/** Listener callback type for generic Molstar viewer actions. */
export type MolstarListener = (action: MolstarAction) => void;
/** Listener callback type for representation/color change broadcasts. */
export type MolstarRepresentationChangeListener = (payload: MolstarRepresentationChangePayload) => void;
/** Listener callback type for structure click events originating from the Molstar viewport. */
export type StructureClickListener = (rowDto: ApoHoloSiteDto) => void;
/** Listener callback type for residue hover events originating from the Molstar viewport. */
export type MolstarHoverListener = (seqId: number | null) => void;

/**
 * Pub/sub event bus that decouples the React table components from the Molstar
 * viewer. Listeners are stored in plain arrays inside `useRef`s (not React state),
 * so dispatching an event never triggers a re-render.
 *
 * Three independent channels:
 * - **dispatch / subscribe** – generic `MolstarAction` events (show/hide/focus, etc.)
 * - **dispatchRepresentationChange / subscribeRepresentationChange** – repr + color changes
 * - **dispatchStructureClick / subscribeStructureClick** – Molstar viewport → table sync
 */
export interface StructureEventBusContextType {
   /** Broadcast a `MolstarAction` to all registered listeners (consumed by `MolstarComponent`). */
   dispatch: (action: MolstarAction) => void;
   /** Broadcast a representation/color change payload to all subscribers. */
   dispatchRepresentationChange: (payload: MolstarRepresentationChangePayload) => void;
   /**
    * Notify all table subscribers that the user clicked a structure in the Molstar viewport.
    * Tables use this to sync their selection state and scroll the clicked row into view.
    */
   dispatchStructureClick: (rowDto: ApoHoloSiteDto) => void;
   /** Register a listener for generic Molstar actions. Returns an unsubscribe function. */
   subscribe: (listener: MolstarListener) => () => void;
   /** Register a listener for representation/color changes. Returns an unsubscribe function. */
   subscribeRepresentationChange: (listener: MolstarRepresentationChangeListener) => () => void;
   /** Register a listener for viewport structure-click events. Returns an unsubscribe function. */
   subscribeStructureClick: (listener: StructureClickListener) => () => void;
   /** Notify all subscribers of a residue hover originating from the Molstar viewport. `null` clears. */
   dispatchMolstarHover: (seqId: number | null) => void;
   /** Register a listener for Molstar residue hover events. Returns an unsubscribe function. */
   subscribeMolstarHover: (listener: MolstarHoverListener) => () => void;
}

export const StructureEventBusContext = createContext<StructureEventBusContextType>({} as StructureEventBusContextType);

/** Convenience hook for consuming `StructureEventBusContext` in function components. */
export const useStructureEventBus = (): StructureEventBusContextType => useContext(StructureEventBusContext);
